import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatAuthError, verifySessionPersisted } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BrandMark from "../components/BrandMark";
import { SupportInline } from "../components/SupportCard";
import CookieWarningBanner from "../components/CookieWarningBanner";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error || "");
  const [busy, setBusy] = useState(false);
  const [cookieBlocked, setCookieBlocked] = useState(!!location.state?.cookieBlocked);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setCookieBlocked(false);
    try {
      const { data } = await api.post("/api/auth/login", { email, password });
      // Verify the browser actually accepted the auth cookie. If not, the
      // user is in strict tracking-prevention mode — show the banner instead
      // of bouncing them to /dashboard where ProtectedRoute will kick them back.
      const persisted = await verifySessionPersisted();
      if (!persisted) {
        setCookieBlocked(true);
        return;
      }
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatAuthError(err, "Sign-in failed. Please try again."));
    } finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden" data-testid="login-page">
      <div className="ambient-orb" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex justify-center mb-7" data-testid="login-brand">
          <BrandMark size={44} />
        </Link>
        {cookieBlocked && (
          <div className="mb-4">
            <CookieWarningBanner onClose={() => setCookieBlocked(false)} />
          </div>
        )}
        <div className="card-surface p-7 sm:p-8">
          <div className="label-mono">Sign in</div>
          <h1 className="text-3xl text-white font-semibold tracking-tight mt-1">Access your terminal</h1>

          <button onClick={googleSignIn} className="mt-6 w-full btn-ghost flex items-center justify-center gap-2.5" data-testid="google-signin-btn">
            <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="#fff" d="M21.6 12.2c0-.7-.06-1.4-.18-2H12v3.78h5.4a4.62 4.62 0 0 1-2 3.04v2.5h3.23c1.89-1.74 2.97-4.3 2.97-7.32z"/><path fill="#fff" d="M12 22c2.7 0 4.96-.9 6.6-2.42l-3.23-2.5c-.9.6-2.04.95-3.37.95-2.6 0-4.8-1.75-5.58-4.1H3.06v2.58A10 10 0 0 0 12 22z"/><path fill="#fff" d="M6.42 13.93c-.2-.6-.32-1.24-.32-1.93s.11-1.33.32-1.93V7.5H3.06A10 10 0 0 0 2 12c0 1.61.39 3.14 1.06 4.5l3.36-2.57z"/><path fill="#fff" d="M12 5.96c1.47 0 2.79.5 3.83 1.5l2.86-2.86A10 10 0 0 0 12 2 10 10 0 0 0 3.06 7.5l3.36 2.57C7.2 7.7 9.4 5.96 12 5.96z"/></svg>
            Continue with Google
          </button>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px bg-white/10 flex-1"></div>
            <span className="label-mono">OR</span>
            <div className="h-px bg-white/10 flex-1"></div>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="label-mono mb-1.5 block">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-glass" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="label-mono">Password</label>
                <Link to="/forgot-password" className="text-xs text-white/60 hover:text-white tracking-tight" data-testid="forgot-link">Forgot?</Link>
              </div>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-glass" data-testid="login-password-input" />
            </div>
            {error && <div className="text-[var(--bear)] text-xs font-mono" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full mt-1" data-testid="login-submit-btn">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-5 text-center text-sm text-white/55">
            Don't have an account?{" "}
            <Link to="/register" className="text-white hover:text-[var(--bull)]" data-testid="signup-link">Create one</Link>
          </div>
        </div>
        <SupportInline className="mt-6" />
      </div>
    </div>
  );
}
